import { ref } from "vue";
import pinia from "@/plugins/PiniaPlugin";

import { goFetch } from "@/utils/network-utils";
import { usePermissionsStore } from "@/stores/Permissions";

import type { ApiFetchResponse, PersistedQueryPayload } from "@tmi/types/api/fetch";
import type { DynamicAdsApiResponse } from "@tmi/types/api/dynamic-ads";
import type { DynamicAd } from "@tmi/types/entities";

import { AdType, SiteLocation } from "@tmi/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

const ads = ref< Array< DynamicAd > >( [] );

function mapAdTypeValue( adTypeValues: Array< string > ) : Array< string > {
    const adTypes: Array< string > = [];

    adTypeValues.forEach( ( value: string ) => {
        if ( AdType.Banner === value || AdType.InPlace === value || AdType.Popup === value
            || AdType.SideLeft === value || AdType.SideRight === value ) {
            adTypes.push( value );
        } else {
            throw new Error( 'Invalid adType value' );
        }
    } );

    return adTypes;
}

function mapSiteLocationCode( siteLocationCodes: Array< string > ) : Array< string > {
    const adTypes: Array< string > = [];

    siteLocationCodes.forEach( ( value: string ) => {
        if ( SiteLocation.Article === value || SiteLocation.Dashboard === value || SiteLocation.MediaPage === value ) {
            adTypes.push( value );
        } else {
            throw new Error( 'Invalid areas_of_site value' );
        }
    } );

    return adTypes;
}

export function useAdService() {
    function fetchAds() {
        goFetch< DynamicAdsApiResponse, PersistedQueryPayload >( {
            serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
            endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
            payload: {
                id: '96b93c1e5ffc22d1064ca07561ccea16',
                variables: { skip: 0, limit: 200 }
            }
        } ).then( ( response: ApiFetchResponse< DynamicAdsApiResponse > ) => {
            if ( null != response?.data?.dynamicAdCatalog ) {
                const catalog = response.data.dynamicAdCatalog.dynamicAds;

                catalog.forEach( ( adResponse ) => ads.value.push( {
                    contentfulId: adResponse.id,
                    title: adResponse.title,
                    body: adResponse.body,

                    priority: adResponse.priority,
                    imageUri: adResponse.image_uri,
                    url: adResponse.url,
                    buttonText: adResponse.button_txt,

                    displayProducts: adResponse.display_shortcodes ?? [],
                    includedProducts: adResponse.included_shortcodes ?? [],
                    excludedProducts: adResponse.excluded_shortcodes ?? [],

                    adTypes: mapAdTypeValue( adResponse.ad_types ),
                    siteLocations: mapSiteLocationCode( adResponse.areas_of_site ),
                    displayDelaySec: adResponse.display_delay_sec
                } ) );
            }
        } ).catch( ( error: any ) => {
            logger.warn( JSON.stringify( error.message ) );
        } );
    }

    function adsFilteredByProductContextAdTypeAndSiteLocation( filterContext: DynamicAdFilterContext ): Array< DynamicAd > {
        const permissionsStore = usePermissionsStore( pinia );
        // SHORTCUT: remove all ads from anyone who assigned Alliance
        if ( permissionsStore.isAllianceMember ) {
            return [];
        }
        return ads.value
            // Filter out any Included Products of which user is not currently a member
            .filter( ( ad: DynamicAd ) =>
                // If there are no included products, do not filter out the advertisement
                0 === ad.includedProducts.length ||
                // Otherwise, filter out any ads that have an included product to which the user is not subscribed
                permissionsStore.hasAccessBasedOnAnyShortCode( ad.includedProducts )
            )
            // Include the ad if the excluded product filter is empty or,,,
            .filter( ( ad: DynamicAd ) =>
                0 === ad.excludedProducts.length ||
                // the excluded product list does not include ANY of the user's subscribed products
                !permissionsStore.hasAccessBasedOnAnyShortCode( ad.excludedProducts )
            )

            // Include the ad in the response...
            .filter( ( ad: DynamicAd ) => {
                const contextProductCodes = filterContext.productShortCodes?.map((code) => code.toLowerCase()) ?? [];

                // If the ad is not restricted to any specific product contexts (ad.displayProducts is empty),
                // it means this ad is general enough to be shown regardless of the page's specific product association.
                if ( ad.displayProducts.length === 0 ) {
                    return true;
                }
                // If the ad IS restricted to specific product contexts (ad.displayProducts is not empty),
                // BUT the current page/context is NOT product-specific (contextProductCodes is empty or undefined),
                // then this ad (which wants a specific product context) should NOT be shown.
                if ( contextProductCodes.length === 0 ) {
                    return false;
                }
                // At this point:
                // 1. The ad IS restricted to specific product contexts (ad.displayProducts is not empty).
                // 2. The current page/context IS product-specific (contextProductCodes is not empty).
                // The ad should be displayed if there's an overlap between the
                // page's associated products and the ad's specified display products.
                return ad.displayProducts.some(
                    ( adDisplayProductShortCode: string ) => contextProductCodes.includes( adDisplayProductShortCode )
                );
            } )
            // Include the ad if the ad type matches the requested context ad type
            .filter( ( ad: DynamicAd ) => ad.adTypes.some( ( adType: string ) => adType.toLowerCase() === filterContext.adType ) )
            // Include the ad if the site location matches the requested context site location
            .filter( ( ad: DynamicAd ) => ad.siteLocations.some( ( siteLocation: string ) => siteLocation.toLowerCase() === filterContext.siteLocation ) );
    }

    // Fire off the loading event when first initialized
    fetchAds();

    return {
        adsFilteredByProductContextAdTypeAndSiteLocation
    };
}

type DynamicAdFilterContext = {
    productShortCodes: string[] | undefined,
    adType: string,
    siteLocation: string
}

export type AdService = ReturnType< typeof useAdService >;